import { omit } from '@app/utils/omit'

import { createApiAction } from '@app/packages/api/utils'
import type { ApiDepersonalizedAnnouncement, ApiInaccurateLocation } from '@app/packages/api/v2/types/entities'
import type { AnnouncementSearchQuery, DepersonalizedAnnouncementQueryRequest } from '@app/packages/api/v2/types/params'
import { v2Path } from '@app/packages/api/v2/utils'

export type DepersonalizedAnnouncementsSearchMeta = { total: number; total_pages: number; current_page: number; cursor: string }

/** A cursor continues a search; a query starts one. */
type SearchRequest = AnnouncementSearchQuery | string

export const getDepersonalizedAnnouncementsByQuery = (request: DepersonalizedAnnouncementQueryRequest) =>
  createApiAction('getDepersonalizedAnnouncementsByQuery', dispatch =>
    dispatch<{ data: ApiDepersonalizedAnnouncement[]; included?: ApiInaccurateLocation[] }>({
      method: 'GET',
      endpoint: v2Path('/api/v2/depersonalized_announcements', request),
    })
  )

export const getDepersonalizedAnnouncementsSearch = (request: SearchRequest) =>
  createApiAction('getDepersonalizedAnnouncementsSearch', dispatch =>
    dispatch<{ data: ApiDepersonalizedAnnouncement[]; meta: DepersonalizedAnnouncementsSearchMeta; included?: ApiInaccurateLocation[] }>({
      method: 'GET',
      endpoint: v2Path('/api/v2/depersonalized_announcements/search', searchQuery(request)),
    })
  )

export const getDepersonalizedAnnouncementsSearchCount = (request: AnnouncementSearchQuery) =>
  createApiAction('getDepersonalizedAnnouncementsSearchCount', dispatch =>
    dispatch<{ data: ApiDepersonalizedAnnouncement[]; meta: DepersonalizedAnnouncementsSearchMeta }>({
      method: 'GET',
      endpoint: v2Path('/api/v2/depersonalized_announcements/search', { ...omit(request, 'created_by'), per_page: 1 }),
    })
  )

/**
 * The same search trimmed to what the map needs. Both types are renamed, because a response
 * carrying two attributes of a location must not pass for the whole entity in the store.
 */
export const getDepersonalizedAnnouncementsSearchMap = (request: SearchRequest) =>
  createApiAction('getDepersonalizedAnnouncementsSearchMap', async dispatch => {
    const resp = await dispatch<{
      data: (Omit<ApiDepersonalizedAnnouncement, 'type'> & { type: 'depersonalized_announcements' })[]
      meta: DepersonalizedAnnouncementsSearchMeta
      included?: (Omit<ApiInaccurateLocation, 'type'> & { type: 'inaccurate_locations' })[]
    }>({
      method: 'GET',
      endpoint: v2Path('/api/v2/depersonalized_announcements/search', {
        ...searchQuery(request),
        order: undefined,
        'fields[depersonalized_announcements]': 'location',
        'fields[inaccurate_locations]': 'rough_latitude,rough_longitude',
        include: 'location',
      }),
    })

    return {
      ...resp,
      data: resp.data.map(item => ({ ...item, type: 'map_depersonalized_announcements' as const })),
      included: (resp.included ?? []).map(location => ({ ...location, type: 'map_inaccurate_locations' as const })),
    }
  })

const searchQuery = (request: SearchRequest) => (typeof request === 'string' ? { cursor: request } : omit(request, 'created_by'))
